/**
 * InitialsCapture — for documents with an Initials field: the signer provides
 * their initials as an image, either TYPED in a handwriting font (defaulting to
 * the SAME font they picked for their signature, if any) or UPLOADED. Emits a
 * PNG data URL via onChange; null until ready. The auto-typed default means it
 * needs no interaction in the common case.
 */
import { useEffect, useRef, useState } from 'react';
import { SIGNATURE_FONTS, paperStyle, renderTypedSignature, type SignatureFont } from './signatureFonts';
import { segGroup, segItemOn, segItemOff } from '../design/ui';

export function InitialsCapture({
  initialsText,
  signatureFont,
  onChange,
  saved,
}: {
  initialsText: string;
  /** The font chosen for the signature (Type option) — used as the default. */
  signatureFont: SignatureFont | null;
  onChange: (dataUrl: string | null) => void;
  /** Reusable profile initials (data URL) — adds a preselected "Use saved" option. */
  saved?: string | null;
}) {
  const [mode, setMode] = useState<'saved' | 'font' | 'upload'>(saved ? 'saved' : 'font');
  // Preselect the saved initials when present.
  useEffect(() => {
    if (saved && mode === 'saved') onChange(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);
  const [font, setFont] = useState<SignatureFont>(signatureFont ?? SIGNATURE_FONTS[0]!);
  const [uploaded, setUploaded] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  // Track whether the user explicitly overrode the font, so a later
  // signature-font change doesn't clobber their choice.
  const overridden = useRef(false);

  // Follow the signature font while the user hasn't overridden it.
  useEffect(() => {
    if (mode === 'font' && !overridden.current && signatureFont) setFont(signatureFont);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureFont]);

  // Render the typed initials whenever font / text changes in font mode.
  useEffect(() => {
    if (mode !== 'font') return;
    let alive = true;
    void renderTypedSignature(initialsText || ' ', font).then((url) => { if (alive) onChange(url); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, font, initialsText]);

  const onFile = (f: File | undefined) => {
    setUploadErr(null);
    if (!f) return;
    if (!['image/png', 'image/jpeg'].includes(f.type)) { setUploadErr('PNG or JPEG only.'); return; }
    if (f.size > 1024 * 1024) { setUploadErr('Max 1 MB.'); return; }
    const r = new FileReader();
    r.onload = () => { const url = String(r.result); setUploaded(url); onChange(url); };
    r.readAsDataURL(f);
  };

  const tab = (active: boolean) => (active ? segItemOn : segItemOff);

  return (
    <div>
      <div className={`${segGroup} mb-2`}>
        {saved && <button type="button" className={tab(mode === 'saved')} onClick={() => { setMode('saved'); onChange(saved); }}>Use saved</button>}
        <button type="button" className={tab(mode === 'font')} onClick={() => setMode('font')}>Type</button>
        <button type="button" className={tab(mode === 'upload')} onClick={() => { setMode('upload'); onChange(uploaded); }}>Upload image</button>
      </div>
      {mode === 'saved' && saved ? (
        <div className="rounded-md border border-dashed border-line-strong bg-surface-2 p-3 flex items-center gap-4">
          <img src={saved} alt="Your saved initials" className="max-h-14 max-w-[140px] object-contain rounded p-1" style={paperStyle} />
          <span className="text-xs text-fg-faint">Your saved initials (manage them on your profile).</span>
        </div>
      ) : mode === 'font' ? (
        <div className="rounded-md border border-dashed border-line-strong bg-surface-2 p-3">
          <div className="flex items-center gap-4 mb-2">
            <span className="text-3xl leading-none rounded px-2 py-1" style={{ ...paperStyle, fontFamily: `"${font.family}"`, fontWeight: font.weight }}>
              {initialsText}
            </span>
            <span className="text-xs text-fg-faint">Your initials{signatureFont ? ' (matching your signature font)' : ''}.</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SIGNATURE_FONTS.map((f) => (
              <button
                key={f.family}
                type="button"
                onClick={() => { overridden.current = true; setFont(f); }}
                className={`px-2 py-1 rounded border text-lg leading-none ${font.family === f.family ? 'border-primary ring-2 ring-primary' : 'border-line hover:border-line-strong'}`}
                style={{ ...paperStyle, fontFamily: `"${f.family}"`, fontWeight: f.weight }}
                title={f.family}
              >
                {initialsText}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-line-strong bg-surface-2 p-3">
          {uploaded ? (
            <div className="flex items-center gap-4">
              <img src={uploaded} alt="Initials" className="max-h-16 max-w-[160px] object-contain rounded p-1" style={paperStyle} />
              <button type="button" className="text-xs text-link underline" onClick={() => { setUploaded(null); onChange(null); }}>Remove</button>
            </div>
          ) : (
            <label className="block text-sm text-fg-muted cursor-pointer">
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              <span className="underline text-link">Choose an image</span> of your initials (PNG/JPEG, max 1 MB).
            </label>
          )}
          {uploadErr && <p className="text-xs text-danger mt-2">{uploadErr}</p>}
        </div>
      )}
    </div>
  );
}
